import { useState } from 'react';
import { api } from '../lib/api.js';
import { periodColor } from '../lib/ui.js';

// ─── Manage Tasks View (desktop) ──────────────────────────────────────────────
export function ManageTasksView({ tasks, locations, onEdit, onDelete, onSopEdit, onSopRemove, user, onRefresh }) {
  const [filter, setFilter] = useState('');
  const [sortCol, setSortCol] = useState('period');
  const [sortDir, setSortDir] = useState('asc');
  // v1.6: bulk-select + bulk-delete (CJ flagged: "task we accidentally added
  // to a wrong store + no way to remove" + "should also have a way to bulk
  // delete. this was a feature we had but it seems to have been lost.")
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bulk delete stays manager-only (manage_tasks); Shift Leads with
  // edit_tasks get the single-row Edit/Del actions only.
  const canBulk = !!user && window.EmpireRoles.hasCapability(user, 'manage_tasks');

  const filtered = tasks
    .filter(t => !filter || t.title.toLowerCase().includes(filter.toLowerCase()) || (t.assigned_role||'').toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      let av, bv;
      if (sortCol === 'period') { av = a.period_sort_order||6; bv = b.period_sort_order||6; }
      else if (sortCol === 'title') { av = a.title; bv = b.title; }
      else if (sortCol === 'role') { av = a.assigned_role||''; bv = b.assigned_role||''; }
      else { av = a.due_time||''; bv = b.due_time||''; }
      const cmp = typeof av === 'number' ? av - bv : av.localeCompare(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const allFilteredSelected = filtered.length > 0 && filtered.every(t => selected.has(t.id));
  function toggleOne(id) {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }
  function toggleAll() {
    setSelected(prev => {
      if (allFilteredSelected) return new Set();
      const s = new Set(prev);
      filtered.forEach(t => s.add(t.id));
      return s;
    });
  }
  async function bulkDelete() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} task${ids.length === 1 ? '' : 's'}? This is reversible — they're soft-deleted (is_active=0) and won't show up in scheduling or completion lists.`)) return;
    setBulkBusy(true);
    try {
      await api('POST', '/api/tasks/bulk-delete', { task_ids: ids },
        user ? { 'x-user-id': String(user.id) } : {});
      setSelected(new Set());
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Bulk delete failed: ' + (e.message || e));
    } finally {
      setBulkBusy(false);
    }
  }

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const Th = ({ col, label, className='' }) => (
    <th className={`text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4 cursor-pointer hover:text-gray-700 select-none whitespace-nowrap ${className}`}
      onClick={() => toggleSort(col)}>
      {label} {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div>
      <div className="mb-4 flex gap-3 items-center flex-wrap">
        <input value={filter} onChange={e => setFilter(e.target.value)}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30"
          placeholder="Filter by name or role..." />
        <span className="text-sm text-gray-400">{filtered.length} tasks</span>
        {canBulk && selected.size > 0 && (
          <button onClick={bulkDelete} disabled={bulkBusy}
            className="px-3 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 active:scale-95 transition-all disabled:opacity-50">
            {bulkBusy ? 'Deleting…' : `Delete ${selected.size} selected`}
          </button>
        )}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full admin-task-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {canBulk && (
                  <th className="py-3 px-3 w-10">
                    <input type="checkbox" checked={allFilteredSelected}
                      onChange={toggleAll} aria-label="Select all" />
                  </th>
                )}
                <Th col="title" label="Task" className="col-task" />
                <Th col="period" label="Period" className="col-mobile-hide" />
                <Th col="role" label="Role" className="col-mobile-hide" />
                <Th col="time" label="Due" className="col-mobile-hide" />
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4 col-mobile-hide">SOP</th>
                <th className="py-3 px-4 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(task => (
                <tr key={task.id} className={`hover:bg-gray-50 transition-colors ${selected.has(task.id) ? 'bg-amber-50' : ''}`}>
                  {canBulk && (
                    <td className="py-3 px-3">
                      <input type="checkbox" checked={selected.has(task.id)}
                        onChange={() => toggleOne(task.id)} aria-label={`Select ${task.title}`} />
                    </td>
                  )}
                  <td className="py-3 px-4 col-task">
                    <p className="font-medium text-sm text-ink task-title">{task.title}</p>
                    {task.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{task.description}</p>}
                  </td>
                  <td className="py-3 px-4 col-mobile-hide">
                    <span className={`period-badge px-2 py-1 rounded-md ${periodColor(task.effective_period || task.time_period, task.period_sort_order || 0)}`}>
                      {task.effective_period || task.time_period}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap col-mobile-hide">
                    {task.assigned_user_id ? `→ ${task.assigned_user_name || 'Staff'}` : (task.assigned_role || '—')}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap col-mobile-hide">{task.due_time || '—'}</td>
                  <td className="py-3 px-4 col-mobile-hide">
                    {task.sop_id ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs bg-gold/10 text-yellow-700 px-2 py-0.5 rounded border border-gold/20 font-medium w-fit">SOP</span>
                        <div className="flex gap-1">
                          <button onClick={() => onSopEdit(task)}
                            className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-all whitespace-nowrap">View/Edit</button>
                          <button onClick={() => onSopRemove(task)}
                            className="px-2 py-1 text-xs font-medium bg-red-50 text-red-500 rounded hover:bg-red-100 transition-all whitespace-nowrap">Remove</button>
                        </div>
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1.5 justify-end">
                      <button onClick={() => onEdit(task)} className="px-2.5 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-all">Edit</button>
                      <button onClick={() => onDelete(task.id)} className="px-2.5 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all">Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={canBulk ? 7 : 6} className="py-10 text-center text-gray-400 text-sm">No tasks match your filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
